import React, { createContext, useContext, useMemo } from "react";
import { useProfile } from "@moonscape/shared";
import { useAuth0 } from "@auth0/auth0-react";

interface OnboardingGateContextValue {
  isInForcedOnboarding: boolean;
  onboardingComplete: boolean;
  isLoading: boolean;
}

const OnboardingGateContext = createContext<OnboardingGateContextValue>({
  isInForcedOnboarding: false,
  onboardingComplete: false,
  isLoading: true,
});

export const useOnboardingGate = () => useContext(OnboardingGateContext);

export const OnboardingGateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading: isAuthLoading, isAuthenticated } = useAuth0();
  const { profile, isLoading: isProfileLoading, isError: isProfileError } = useProfile();

  const isLoading = isAuthLoading || isProfileLoading;

  // Single source of truth from DB — quiz sets tax_jurisdiction_id on completion
  // CPA/accountant users skip jurisdiction step, so isAccountant or quiz completion counts
  const onboardingComplete = useMemo(() => {
    return Boolean(profile?.taxJurisdiction)
      || Boolean(profile?.isAccountant)
      || Boolean(profile?.attributes?.quiz?.completedAt);
  }, [profile?.taxJurisdiction, profile?.isAccountant, profile?.attributes?.quiz?.completedAt]);

  // If the profile fetch errored we can't know onboarding state — fail open so a
  // backend outage never strips the nav chrome and strands the user.
  // Logged out there is no profile to read: absence is not a verdict (ui-state-invariants),
  // or "/" sends a logged-out session to /quiz and a second login redirect races App's.
  const isInForcedOnboarding = isAuthenticated && !isLoading && !isProfileError && !onboardingComplete;

  const value = useMemo(() => ({
    isInForcedOnboarding,
    onboardingComplete,
    isLoading,
  }), [isInForcedOnboarding, onboardingComplete, isLoading]);

  return (
    <OnboardingGateContext.Provider value={value}>
      {children}
    </OnboardingGateContext.Provider>
  );
};
